import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

// Generate or get visitor ID
const getVisitorId = () => {
  let visitorId = localStorage.getItem('zazu_visitor_id');
  if (!visitorId) {
    visitorId = 'v_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
    localStorage.setItem('zazu_visitor_id', visitorId);
  }
  return visitorId;
};

// Generate session ID (new for each browser session)
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('zazu_session_id');
  if (!sessionId) {
    sessionId = 's_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
    sessionStorage.setItem('zazu_session_id', sessionId);
  }
  return sessionId;
};

// Detect device type
const getDeviceType = () => {
  const ua = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return 'tablet';
  }
  if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
    return 'mobile';
  }
  return 'desktop';
};

// Get browser name
const getBrowser = () => {
  const ua = navigator.userAgent;
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Safari')) return 'Safari';
  if (ua.includes('Edge')) return 'Edge';
  if (ua.includes('Opera')) return 'Opera';
  return 'Unknown';
};

// Get OS
const getOS = () => {
  const ua = navigator.userAgent;
  if (ua.includes('Windows')) return 'Windows';
  if (ua.includes('Mac')) return 'macOS';
  if (ua.includes('Linux')) return 'Linux';
  if (ua.includes('Android')) return 'Android';
  if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
  return 'Unknown';
};

export const usePageTracking = () => {
  const location = useLocation();
  const { api } = useAuth();
  const lastPath = useRef('');
  const pageStartTime = useRef(Date.now());

  useEffect(() => {
    const trackPageView = async () => {
      // Don't track same page twice
      if (location.pathname === lastPath.current) return;
      
      // Track time on previous page
      const timeOnPage = lastPath.current ? Math.floor((Date.now() - pageStartTime.current) / 1000) : 0;
      
      try {
        await api.post('/analytics/track', null, {
          params: {
            page_path: location.pathname,
            page_title: document.title,
            referrer: document.referrer || '',
            device_type: getDeviceType(),
            browser: getBrowser(),
            os: getOS(),
            session_id: getSessionId(),
            time_on_page: timeOnPage > 0 ? timeOnPage : null
          }
        });
      } catch (error) {
        // Silently fail - analytics shouldn't break the app
        console.debug('Analytics tracking failed:', error);
      }
      
      lastPath.current = location.pathname;
      pageStartTime.current = Date.now();
    };

    trackPageView();
  }, [location.pathname, api]);
};

export default usePageTracking;
